import { useCallback, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { AiChatStreamEvent } from '@apis/aiAgent';
import { isAnswerFrame, replaceLastAssistant, withAnswerFrame, type IChatMessage } from '@utils/aiChat';

export interface IAiTurnControl {
  /** The live stream's controller, so a stop, a reset or a thread change can cancel it. */
  abortRef: MutableRefObject<AbortController | null>;
  /** Rewrite the answer being written — never a user bubble (`replaceLastAssistant`). */
  updateLastAssistant: (updater: (last: IChatMessage) => IChatMessage) => void;
  /**
   * Write a frame into the answer if it is one of the answer's own (prose, a
   * drawing, a link, the receipt, follow-ups); false for a frame about the turn,
   * which the surface handles itself.
   */
  writeAnswer: (event: AiChatStreamEvent) => boolean;
  /** The reader's stop: cancel the turn and mark its answer stopped. */
  stop: () => void;
}

/**
 * The handle on an assistant turn in flight, shared by both chat surfaces — the
 * AI Agent page (`useAgentTurn`) and the dashboard card (`useAiChat`) — which
 * stop a turn the same way and must keep doing so.
 *
 * Stopping IS cancelling the request: the server aborts the model call when the
 * connection closes, and a turn that did not finish stores no answer. The turn's
 * own `finally` settles the rest; `stop` only marks the bubble, so the gap it
 * leaves reads as the reader's doing rather than a silent failure.
 */
export function useAiTurnControl(setMessages: Dispatch<SetStateAction<IChatMessage[]>>): IAiTurnControl {
  const abortRef = useRef<AbortController | null>(null);

  // The streaming callback outlives the render that opened it, so it rewrites
  // through an updater rather than reading `messages` it would hold stale.
  const updateLastAssistant = useCallback(
    (updater: (last: IChatMessage) => IChatMessage) =>
      setMessages((prev) => replaceLastAssistant(prev, updater)),
    [setMessages],
  );

  const writeAnswer = useCallback(
    (event: AiChatStreamEvent) => {
      if (!isAnswerFrame(event)) return false;
      updateLastAssistant((last) => withAnswerFrame(last, event));
      return true;
    },
    [updateLastAssistant],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    updateLastAssistant((last) => ({ ...last, stopped: true }));
  }, [updateLastAssistant]);

  return { abortRef, updateLastAssistant, writeAnswer, stop };
}
